import {
    ProFormCheckbox,
    ProFormDatePicker,
    ProFormDependency,
    ProFormRadio,
    ProFormSelect,
    ProFormText,
    ProFormTreeSelect,
} from '@ant-design/pro-form';
import {useCallback, useContext, useEffect, useState} from 'react';
import {DDEXPageContext} from './DDEXMainPage';
import ProCard from '@ant-design/pro-card';
import {checksEnabled, fetchAssetLabelOptions, fetchLabelOptions} from './ReleaseEntryStepForm';
import axiosApiInstance from '../../../api/axiosClient';
import NewArtistForm from './NewArtistForm';
import NewLabelForm from './NewLabelForm';
import {defaultSelectionsAtom, isReleaseInfoLoadingAtom, newReleaseUploadQueueAtom, warningsAtom} from './ddex_atoms';
import ProFormDatePickerYear from '@ant-design/pro-form/es/components/DatePicker/YearPicker';
import {Button, Image, Spin, TreeSelect, message, Alert, Form} from 'antd';
import {Store} from 'antd/es/form/interface';
import {atom, useAtom, useAtomValue} from 'jotai';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowsRotate, faSave} from '@fortawesome/free-solid-svg-icons';
import {useQuery} from '@tanstack/react-query';
import {uploadedFilesAtom} from './FileUploadStep';
import {
    countryTreeOptions,
    genreOptions,
    genreToSubgenreOptions,
    releasePriceTierOptions
} from '../../../common/ddex_options'
import { artistOptionsAtom } from './ddex_atoms';
import { artistSelectSearchFieldProps } from './artistSearchUtils';
import {
    releaseInfoFormAtom,
    artTracksFormAtom,
    isAntFormInstance,
    useArtTracksForm,
    previewImageAtom
} from './release_form_atoms';
import dayjs from 'dayjs';


const createArtistDisplayString = (main_artists: string[], featuring_artists: string[]): string => {
    var s = '';
    if (!main_artists || main_artists.length === 0) {
        return s;
    }

    var delim = ', ';
    if (main_artists.length === 2) delim = ' & ';

    s = s + main_artists.join(delim);

    return s;
};

type UPC_RESPONSE = {
    upc: string;
    sequence: number;
    checksum_digit: number;
    is_user: boolean;
};

export const generatedUPCAtom = atom('');

// Fetch image URL for preview
export const getImageUrl = async (filename: string, id: string) => {
    try {
        const response = await axiosApiInstance.post(
            '/api/products/releases/generate-presigned-url-get',
            {
                fileName: filename,
                folderName: id
            }
        );
        return response.data.presignedUrl;
    } catch (err) {
        console.error('Error getting image URL:', err);
        return '';
    }
};

const StepsFormFirstPage = ({initialValues, edit, fetchReleaseData}: StepsFormFirstPageProps) => {
    const {stepFormRef, listRef, setArtists} =
        useContext(DDEXPageContext);

    const [, setDefaultSelections] = useAtom(defaultSelectionsAtom);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [generatedUPC, setGeneratedUPC] = useAtom(generatedUPCAtom);
    const [uploadedFiles] = useAtom(uploadedFilesAtom);
    const [isReleaseInfoLoading, setIsReleaseInfoLoading] = useAtom(isReleaseInfoLoadingAtom);
    const [warnings, setWarnings] = useAtom(warningsAtom);
    const [previewImage, setPreviewImage] = useAtom(previewImageAtom);
    const [previewImageSrc, setPreviewImageSrc] = useState<string>('');

    const releaseInfoForm = Form.useFormInstance();
    const artTracksForm = useAtomValue(useArtTracksForm);
    const [releaseInfoData, setReleaseInfoData] = useAtom(releaseInfoFormAtom);
    const [artTracksData, setArtTracksData] = useAtom(artTracksFormAtom);

    // Watch for changes in album cover filename
    const albumCoverFilename = Form.useWatch('album_cover_filename', releaseInfoForm);


    // Filter only image files for the cover art dropdown
    const imageFiles = uploadedFiles.filter(file => file.fileType === 'image');

    const upcQuery = useQuery({
        queryKey: ['generated_upc'],
        queryFn: () =>
            axiosApiInstance.get<UPC_RESPONSE>('/api/products/new_upc').then((res) => {
                setGeneratedUPC(res.data.upc);
                releaseInfoForm.setFieldValue('upc_ean', res.data.upc);
                // Update the atom with the new data
                setReleaseInfoData({...releaseInfoData, upc_ean: res.data.upc});
            }),
        enabled: false
    });

    const {SHOW_PARENT} = TreeSelect;    useEffect(() => {
        // Get ID from URL if it exists
        const urlParams = new URLSearchParams(window.location.search);
        const idFromUrl = urlParams.get('id');

        if (idFromUrl) {
            releaseInfoForm.setFieldValue('id', idFromUrl);
            // Update the atom with the new data
            setReleaseInfoData({...releaseInfoData, id: idFromUrl});
            console.log('Set ID from URL:', idFromUrl);
        }

        return () => {
            setGeneratedUPC('');
        };
    }, []);

    useEffect(() => {
        console.log('rendering first page');
        // Extract ID from URL path (format: /music/create/{id})
        const pathParts = window.location.pathname.split('/');
        const idFromUrl = pathParts[pathParts.length - 1];

        if (idFromUrl && /^\d+$/.test(idFromUrl)) {
            console.log('Found ID in URL path:', idFromUrl);
            // Set the ID field in the form
            releaseInfoForm.setFieldValue('id', idFromUrl);
            // Update the atom with the new data
            setReleaseInfoData({...releaseInfoData, id: idFromUrl});

            // Fetch existing release data if ID is present
            fetchReleaseData(idFromUrl);
        }

        return () => {
            setGeneratedUPC('');
        };
    }, []);

    useEffect(() => {
        const updatePreview = async () => {
            const filename = releaseInfoForm.getFieldValue('album_cover_filename');
            if (filename) {
                const imageUrl = await getImageUrl(filename, releaseInfoForm.getFieldValue('id'));
                setPreviewImageSrc(imageUrl);
            }
        };
        updatePreview();
        // Update preview when album cover filename changes
    }, [albumCoverFilename]);


    const handleFirstPageFlip = async (
        main_artists: API.MusicDistribution.Artist[],
        featuring_artists: API.MusicDistribution.Artist[]
    ) => {
        if (!isAntFormInstance(artTracksForm)) {
            return;
        }
        const tracks = artTracksForm.getFieldValue('art_tracks');
        artTracksForm.setFieldValue(
            'art_tracks',
            tracks.map((track: API.DDEX.Track) => ({...track, main_artists, featuring_artists}))
        );
        // Update the atom with the new data
        setArtTracksData({...artTracksData, art_tracks: tracks.map((track: API.DDEX.Track) => ({...track, main_artists, featuring_artists}))});
    };

    const handleTrackNumbers = useCallback(
        (n: number, noOfTracks: number) => {
            if (n > noOfTracks) {
                listRef.current.add(
                    {isrc: '', track_artist_id: releaseInfoForm.getFieldValue('main_artist_id')},
                    n
                );
            } else {
                listRef.current.remove(n);
            }
        },
        [listRef, releaseInfoForm]
    );

    const handlePreview = async (imageUrl: string, filename: string) => {
        setPreviewImage(imageUrl);
        setPreviewOpen(true);
    };



    const artistOptions = useAtomValue(artistOptionsAtom);    // Function to save only release level information

    return (
        <>
            <Spin spinning={isReleaseInfoLoading} tip="Loading release information..." size="large">
                <ProFormText name="id" hidden />
                {warnings.length > 0 && (
                    <Alert
                        message="Validation Warnings"
                        description={
                            <ul>
                            {warnings.map((warning, index) => (
                                <li key={index}>
                                    <strong>{warning.field}:</strong> {warning.message} 
                                    {warning.group !== 'general' && <span> ({warning.group})</span>}
                                </li>
                            ))}
                        </ul>
                    }
                    type="warning"
                    showIcon
                    closable
                    onClose={() => setWarnings([])}
                    style={{ marginBottom: 16 }}
                />
                )}
            <ProCard colSpan={24} direction='row' style={{width: '100%'}} split='vertical'>
                <ProCard colSpan={12}>
                    <ProFormText
                        name={'name'}
                        label='Release Title'
                        placeholder={'Main Title of Release'}
                        fieldProps={{
                            onChange(e) {
                                const name = e?.target?.value ?? e?.currentTarget?.value ?? '';
                                setReleaseInfoData((prev) => ({...prev, name}));
                            },
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.reject('The release title is empty. Please provide a release title');
                                    }
                                    if (value.length > 255) {
                                        return Promise.reject('The release title contains more than 255 characters and it\'s not allowed by our system. Please provide a shorter title');
                                    }
                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormText
                        name={'subtitle'}
                        label='Version/Subtitle'
                        placeholder={'Release Subtitle (optional)'}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value && value.length > 255) {
                                        return Promise.reject('The release version contains more than 255 characters and it\'s not managed by our system. Please provide a shorter version.');
                                    }
                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormText
                        name={'upc_ean'}
                        label='UPC/EAN'
                        placeholder={`Leave empty to generate one automatically`}
                        disabled={edit}
                        fieldProps={{
                            onChange(e) {
                                if ((e.currentTarget || e.target.value === '') && generatedUPC !== '') {
                                    axiosApiInstance.put(`/api/products/unbind_upc/${generatedUPC}`);
                                    setGeneratedUPC('');
                                }
                            }
                        }}
                        formItemProps={{
                            extra: edit || (
                                <Button
                                    type='text'
                                    size='small'
                                    loading={upcQuery.isFetching}
                                    onClick={async () => {
                                        if (generatedUPC != '') {
                                            releaseInfoForm.setFieldValue('upc_ean', generatedUPC);
                                            // Update the atom with the new data
                                            setReleaseInfoData({...releaseInfoData, upc_ean: generatedUPC});
                                            return;
                                        }
                                        upcQuery.refetch();
                                    }}
                                    icon={
                                        <FontAwesomeIcon
                                            style={{
                                                marginTop: 6,
                                                marginRight: '4px'
                                            }}
                                            icon={faArrowsRotate}
                                        />
                                    }>
                                    Generate New UPC
                                </Button>
                            )
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    const upc = value as string;
                                    if (!upc) return Promise.resolve();

                                    if (typeof upc !== 'string' || upc.length !== 13 || !/^\d{13}$/.test(upc)) {
                                        return Promise.reject('UPC needs to have 13 numerical digits');
                                    }

                                    const digits = upc.split('').map(Number);
                                    const checkDigit = digits.pop();
                                    const sum = digits.reduce((acc, digit, index) => {
                                        return acc + (index % 2 === 0 ? digit * 1 : digit * 3);
                                    }, 0);
                                    const calculatedCheckDigit = (10 - (sum % 10)) % 10;

                                    if (calculatedCheckDigit !== checkDigit) {
                                        return Promise.reject('Invalid UPC. The code you entered does not meet the requirements');
                                    }
                                    return Promise.resolve();
                                },
                                validateTrigger: 'onChange',
                                warningOnly: true
                            }
                        ]}
                    />
                    <ProFormSelect
                        name={'artists'}
                        label='Main Artists'
                        mode='multiple'
                        options={artistOptions} // Changed from request={fetchArtistOptions}
                        fieldProps={{
                            ...artistSelectSearchFieldProps,
                            onChange(value) {
                                const main_artists = value;
                                const featuring_artists =
                                    releaseInfoForm.getFieldValue('featuring_artists');

                                const artist_display = createArtistDisplayString(main_artists, featuring_artists);
                                releaseInfoForm.setFieldValue('artist_display', artist_display);
                                // Update the atom with the new data
                                setReleaseInfoData({...releaseInfoData, artist_display, artists: value});

                                setDefaultSelections((prev) => ({...prev, main_artists: value}));
                            },
                            dropdownRender(menu) {
                                return (
                                    <>
                                        {menu}
                                        <NewArtistForm stepFormRef={stepFormRef}/>
                                    </>
                                );
                            }
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value || (Array.isArray(value) && value.length === 0)) {
                                        return Promise.reject('The artist name is empty. Please provide a primary artist at release level');
                                    }

                                    // Check if any selected artists have validation warnings
                                    const artistWarnings = warnings.filter(
                                        warning => warning.field === 'artists' && warning.group === 'believe'
                                    );

                                    if (artistWarnings.length > 0) {
                                        return Promise.reject(
                                            'Some artists have missing or invalid Spotify/Apple Music IDs for Believe. Check validation warnings for details.'
                                        );
                                    }

                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormCheckbox
                        name={'is_compilation'}
                        label={'Various Artists/Compilation'}
                        placeholder={'Various Artists/Compilation'}
                    />
                    <ProFormSelect
                        name={'featuring_artists'}
                        label='Featuring Artists'
                        mode='multiple'
                        options={artistOptions} // Changed from request={fetchArtistOptions}
                        fieldProps={{
                            ...artistSelectSearchFieldProps,
                            onChange(value) {
                                setArtists((prev) => ({...prev, featuring_artists: value}));
                                setDefaultSelections((prev) => ({...prev, featuring_artists: value}));
                            },
                            dropdownRender(menu) {
                                return (
                                    <>
                                        {menu}
                                        <NewArtistForm stepFormRef={stepFormRef}/>
                                    </>
                                );
                            }
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    // Only validate if there are featuring artists
                                    if (!value || (Array.isArray(value) && value.length === 0)) {
                                        return Promise.resolve();
                                    }

                                    // Check if any selected featuring artists have validation warnings
                                    const artistWarnings = warnings.filter(
                                        warning => warning.field === 'featuring_artists' && warning.group === 'believe'
                                    );

                                    if (artistWarnings.length > 0) {
                                        return Promise.reject(
                                            'Some featuring artists have missing or invalid Spotify/Apple Music IDs for Believe. Check validation warnings for details.'
                                        );
                                    }

                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormText name={'artist_display'} label='Artist Display Name'/>
                    <ProFormSelect
                        name={'label_id'}
                        label='Label'
                        placeholder={`Believe / store display label (e.g. Prime Productions)`}
                        tooltip='Believe uses Label. YouTube uses Asset Label separately — do not pick the CamelCase YouTube name here.'
                        request={fetchLabelOptions}
                        fieldProps={{
                            showSearch: true,
                            dropdownRender(menu) {
                                return (
                                    <>
                                        {menu}
                                        <NewLabelForm stepFormRef={stepFormRef}/>
                                    </>
                                );
                            }
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.reject('The album Label is empty. Please provide a label at release level.');
                                    }
                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormSelect
                        name={'asset_label_id'}
                        label='Asset Label'
                        placeholder={`YouTube Asset Label (e.g. PrimeProductions)`}
                        tooltip='YouTube-only. Separate from Believe Label.'
                        request={fetchAssetLabelOptions}
                        fieldProps={{
                            showSearch: true
                        }}
                    />
                    <ProFormText
                        name='p_line'
                        label='℗ Line'
                        placeholder='e.g. 2026 Your Company'
                        tooltip='Stored on the label record. Not auto-filled from the label name when you change label.'
                    />
                    <ProFormText
                        name='c_line'
                        label='© Line'
                        placeholder='e.g. 2026 Your Company'
                        tooltip='Stored on the label record. Not auto-filled from the label name when you change label.'
                    />
                    <ProFormSelect
                        name='genre'
                        label='Genre'
                        options={genreOptions}
                        showSearch
                        fieldProps={{
                            onChange(value, option) {
                                releaseInfoForm.setFieldValue('sub_genre', undefined);
                                // Update the atom with the new data
                                setReleaseInfoData({...releaseInfoData, sub_genre: undefined, genre: value});
                            },
                            onClear() {
                                releaseInfoForm.setFieldValue('sub_genre', undefined);
                                // Update the atom with the new data
                                setReleaseInfoData({...releaseInfoData, sub_genre: undefined, genre: undefined});
                            }
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.reject('The main genre cannot be empty. Please provide a main genre at release level.');
                                    }
                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormDependency name={['genre']}>
                        {({genre}) => (
                            <ProFormSelect
                                name='sub_genre'
                                label='Subgenre'
                                options={genreToSubgenreOptions[genre]}
                                showSearch
                            />
                        )}
                    </ProFormDependency>
                </ProCard>
                <ProCard>
                    <ProFormRadio.Group
                        name='type'
                        label='Format'
                        radioType='button'
                        initialValue={'ALBUM'}
                        options={['ALBUM', 'SINGLE', 'EP']}
                        fieldProps={{
                            onChange(e) {
                                const price_options = releasePriceTierOptions[e.target.value];
                                releaseInfoForm.setFieldValue('price_tier', price_options[0].value);
                                // Update the atom with the new data
                                setReleaseInfoData({...releaseInfoData, price_tier: price_options[0].value, type: e.target.value});
                            }
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.reject('The album format cannot be empty. Please provide an album format');
                                    }
                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormDatePicker
                        name='digital_release_date'
                        label='Digital Release Date'
                        placeholder={'Release Date'}
                        normalize={(value) => value?.format('YYYY-MM-DDT00:00:00+00:00')}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.reject('The digital release date cannot be empty. Please provide a date.');
                                    }
                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormDatePicker
                        name='original_release_date'
                        label='Original Release Date'
                        placeholder={'Physical/Original Release Date'}
                        normalize={(value) => value?.format('YYYY-MM-DDT00:00:00+00:00')}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.reject('The album original release date cannot be empty. Please provide a date.');
                                    }
                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormDatePickerYear
                        label='Production Year'
                        name='production_year'
                        normalize={(value) => value?.format('YYYY-MM-DDT00:00:00+00:00')}
                        fieldProps={{
                            renderExtraFooter: (mode) => ( // `mode` can be 'date', 'month', 'year', etc.
                                <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => {
                                            const form = releaseInfoForm; // Or stepFormRef.current if that's how you access it

                                            form.setFieldValue('production_year', dayjs());
                                        }}
                                    >
                                        Current Year
                                    </Button>
                                </div>
                            ),
                        }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.reject('The production year is empty. Please provide a production year at release level.');
                                    }

                                    // If value is a dayjs object, use its year() method. Otherwise, parse from string.
                                    const year = dayjs(value).year();
                                    const currentYear = new Date().getFullYear();

                                    if (year < 1925 || year > currentYear + 1) {
                                        return Promise.reject(`The production year cannot be early 1925 and after Year ${currentYear + 1}. Please provide a correct value.`);
                                    }

                                    return Promise.resolve();
                                },
                                warningOnly: true,
                                validateTrigger: 'onChange'
                            }
                        ]}
                    />
                    <ProFormDependency name={['type']}>
                        {({type}) => {
                            const options = releasePriceTierOptions[type];
                            return (
                                <ProFormSelect
                                    label='Price Tier'
                                    name='price_tier'
                                    initialValue={options ? options[0].value : undefined}
                                    options={options}
                            />
                            );
                        }}
                    </ProFormDependency>
                    <ProFormTreeSelect
                        label='Release Territories'
                        name='territories'
                        initialValue={'Worldwide'}
                        request={async () => countryTreeOptions}
                        fieldProps={{
                            showCheckedStrategy: SHOW_PARENT,
                            showSearch: true,
                            treeCheckable: true,
                            maxTagCount: 5
                        }}
                    />
                    <ProFormSelect
                        label='Cover Art'
                        name='album_cover_filename'
                        options={imageFiles.map(file => ({
                            label: file.name,
                            value: file.name
                        }))}
                        fieldProps={{
                            onChange: async (value) => {
                                const imageUrl = await getImageUrl(value, releaseInfoForm.getFieldValue('id'));
                                setPreviewImageSrc(imageUrl);
                            }
                        }}
                    />
                    {previewImageSrc && (
                        <div style={{marginTop: 16, textAlign: 'center'}}>
                            <Image
                                src={previewImageSrc}
                                alt="Cover Art Preview"
                                style={{maxWidth: '300px'}}
                                preview={{
                                    visible: previewOpen,
                                    onVisibleChange: (visible) => setPreviewOpen(visible)
                                }}
                            />
                        </div>
                    )}
                    {previewImage && (
                        <Image
                            wrapperStyle={{display: 'none'}}
                            preview={{
                                visible: previewOpen,
                                onVisibleChange: (visible) => setPreviewOpen(visible)
                            }}
                            src={previewImage}
                        />
                    )}
                </ProCard>
            </ProCard>
            </Spin>
        </>
    );
};

type StepsFormFirstPageProps = {
    initialValues?: Store;
    edit: boolean;
    fetchReleaseData?: (id: string) => void;
};

export default StepsFormFirstPage;
